// eBay refused the reply on the return (2026-11-04, docs/contact-view.md
// "The reply's destination"; docs/decisions/2026-11-04-ebay-return-reply-
// refused.md). The return is at a stage that takes no messages (eBay 1502),
// so the reply was NOT sent. One compact orange box above the reply with one
// blue button, "Send this reply to the buyer as an eBay message", which
// sends what is in the reply box at the moment it is clicked. After the
// send, the green confirmation takes its place. The panel's 13 px text, only
// the header line bold, small icons (AgentDraftLayout.jsx's A2 tokens).
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { A2 } from './AgentDraftLayout.jsx'
import { RETURN_REPLY_REFUSED_TITLE, RETURN_REPLY_REFUSED_WHAT_TO_DO, SEND_AS_EBAY_MESSAGE_LABEL, returnReplyRefusedReason, returnReplyResentConfirmation } from '@cxhub/shared'

const dark = (t) => t.palette.mode === 'dark'
const WARN = {
  bg: (t) => (dark(t) ? 'rgba(194, 65, 12, 0.14)' : '#FFF4E5'),
  border: (t) => (dark(t) ? t.palette.warning.main : '#C2410C'),
  head: (t) => (dark(t) ? t.palette.warning.light : '#7C2D12'),
  text: (t) => (dark(t) ? t.palette.text.primary : '#431407'),
}
const OK = {
  bg: (t) => (dark(t) ? 'rgba(21, 128, 61, 0.14)' : '#ECFDF3'),
  border: (t) => (dark(t) ? t.palette.success.main : '#15803D'),
  text: (t) => (dark(t) ? t.palette.success.light : '#14532D'),
}

function WarnIcon() {
  return (
    <Box component="svg" aria-hidden="true" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} sx={{ flexShrink: 0, mt: '1px', color: WARN.border }}>
      <path d="M12 3 2 21h20L12 3z" /><line x1="12" y1="10" x2="12" y2="14" /><line x1="12" y1="17.5" x2="12" y2="17.6" />
    </Box>
  )
}
function SendIcon() {
  return (
    <Box component="svg" aria-hidden="true" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} sx={{ flexShrink: 0 }}>
      <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" />
    </Box>
  )
}
function CheckIcon() {
  return (
    <Box component="svg" aria-hidden="true" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} sx={{ flexShrink: 0, mt: '1px', color: OK.border }}>
      <circle cx="12" cy="12" r="10" /><polyline points="7 12.5 10.5 16 17 9" />
    </Box>
  )
}

const TEXT = { fontSize: 13, lineHeight: 1.45 }

// refusal: the stored returnReplyRefused entry ({ kind, id, ebayCode,
// stateWords }). error: the button's last failure, shown under it.
export function ReturnReplyRefusedNotice({ refusal, busy = false, disabled = false, error = null, onSend }) {
  if (!refusal) return null
  return (
    <Box role="alert" data-testid="return-reply-refused" sx={{ bgcolor: WARN.bg, border: 1, borderColor: WARN.border, borderRadius: '6px', px: '12px', py: '10px', display: 'flex', flexDirection: 'column', gap: '8px', mb: 0.75 }}>
      <Stack direction="row" alignItems="flex-start" spacing="8px">
        <WarnIcon />
        <Stack spacing="2px" sx={{ minWidth: 0 }}>
          <Typography sx={{ ...TEXT, fontWeight: 700, color: WARN.head }}>{RETURN_REPLY_REFUSED_TITLE}</Typography>
          <Typography sx={{ ...TEXT, color: WARN.text }} data-testid="return-reply-refused-reason">{returnReplyRefusedReason(refusal)}</Typography>
          <Typography sx={{ ...TEXT, color: WARN.text, mt: '4px' }}>What to do: {RETURN_REPLY_REFUSED_WHAT_TO_DO}</Typography>
        </Stack>
      </Stack>
      <Button variant="contained" disableElevation onClick={onSend} disabled={busy || disabled} data-testid="send-as-ebay-message"
        startIcon={busy ? <CircularProgress size={12} color="inherit" /> : <SendIcon />}
        sx={{ alignSelf: 'flex-start', ml: '24px', textTransform: 'none', fontSize: 13, fontWeight: 600, py: '4px', px: '12px', borderRadius: '6px', bgcolor: A2.primary, '& .MuiButton-startIcon': { mr: '6px' } }}>
        {SEND_AS_EBAY_MESSAGE_LABEL}
      </Button>
      {error && <Typography sx={{ ...TEXT, color: 'error.main', ml: '24px', whiteSpace: 'pre-wrap' }} data-testid="send-as-ebay-message-error">{error}</Typography>}
    </Box>
  )
}

// The green confirmation after the button's send. sent: the route's
// sentAsEbayMessage ({ buyerId, at, stateWords }).
export function ReturnReplyResentNotice({ sent }) {
  if (!sent) return null
  const at = sent.at ? new Date(sent.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'now'
  return (
    <Box role="status" data-testid="return-reply-resent" sx={{ bgcolor: OK.bg, border: 1, borderColor: OK.border, borderRadius: '6px', px: '12px', py: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px', mb: 0.75 }}>
      <CheckIcon />
      <Stack spacing="2px" sx={{ minWidth: 0 }}>
        <Typography sx={{ ...TEXT, fontWeight: 700, color: OK.text }}>Reply sent to the buyer as an eBay message</Typography>
        <Typography sx={{ ...TEXT, color: OK.text }}>{returnReplyResentConfirmation({ buyerId: sent.buyerId, at, stateWords: sent.stateWords })}</Typography>
      </Stack>
    </Box>
  )
}
